import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { parseAsInteger, useQueryState } from 'nuqs';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { reviewInput, type Paginated } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { EmptyState, ErrorState, LoadingState } from '@/components/shared/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import { useTimezone } from '@/lib/org';
import type { ReviewRow } from './types';

type Values = z.input<typeof reviewInput>;

function ReviewModal({ open, onOpenChange, review }: { open: boolean; onOpenChange: (v: boolean) => void; review: ReviewRow | null }) {
  const qc = useQueryClient();
  const form = useForm<Values, unknown, z.output<typeof reviewInput>>({ resolver: zodResolver(reviewInput), defaultValues: { customerName: '', rating: 5, comment: '', publicationAuthorized: false } });
  const { errors, isSubmitting } = form.formState;
  useEffect(() => {
    if (open) form.reset(review ? { customerName: review.customerName, rating: review.rating, comment: review.comment, publicationAuthorized: review.publicationAuthorized } : { customerName: '', rating: 5, comment: '', publicationAuthorized: false });
  }, [open, review, form]);

  async function submit(v: z.output<typeof reviewInput>) {
    try {
      if (review) await api.patch(`/site/reviews/${review.id}`, v);
      else await api.post('/site/reviews', v);
      await qc.invalidateQueries({ queryKey: ['site', 'reviews'] });
      toast.success(review ? 'Avaliação atualizada' : 'Avaliação cadastrada');
      onOpenChange(false);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }
  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={review ? 'Editar avaliação' : 'Nova avaliação'}
      size="sm"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="submit" form="review-form" disabled={isSubmitting}>Salvar avaliação</Button>
        </>
      }
    >
      <form id="review-form" onSubmit={form.handleSubmit(submit)} className="grid gap-4" noValidate>
        <Field id="rv-name" label="Nome do cliente" error={errors.customerName && 'Informe o nome (2 a 80 letras).'}>
          <Input id="rv-name" maxLength={80} {...form.register('customerName')} />
        </Field>
        <Controller
          control={form.control}
          name="rating"
          render={({ field }) => (
            <fieldset className="grid gap-1.5">
              <legend className="text-sm font-medium">Nota</legend>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" aria-label={`${n} estrela${n > 1 ? 's' : ''}`} aria-pressed={field.value === n} onClick={() => field.onChange(n)} className="rounded p-1 focus-visible:ring-2 focus-visible:ring-ring">
                    <Star className="size-6 text-brass" style={{ fill: n <= field.value ? 'currentColor' : 'transparent' }} aria-hidden />
                  </button>
                ))}
              </div>
            </fieldset>
          )}
        />
        <Field id="rv-comment" label="Comentário" error={errors.comment && 'Escreva de 3 a 600 caracteres.'}>
          <Textarea id="rv-comment" rows={4} maxLength={600} {...form.register('comment')} />
        </Field>
        <label className="flex items-start gap-2.5 text-sm">
          <input type="checkbox" className="mt-0.5 size-4 accent-[var(--bottle)]" {...form.register('publicationAuthorized')} />
          <span>
            O cliente autorizou a publicação no site
            <span className="block text-xs text-muted-foreground">Sem esta marcação a avaliação não aparece para os visitantes.</span>
          </span>
        </label>
      </form>
    </FormModal>
  );
}

export function ReviewsTab() {
  const qc = useQueryClient();
  const tz = useTimezone();
  const [page, setPage] = useQueryState('pa', parseAsInteger.withDefault(1));
  const [editing, setEditing] = useState<ReviewRow | null>(null);
  const [open, setOpen] = useState(false);
  const q = useQuery({ queryKey: ['site', 'reviews', page], queryFn: () => api.get<Paginated<ReviewRow>>('/site/reviews', { page, pageSize: 20 }), placeholderData: (p) => p });

  async function toggle(r: ReviewRow) {
    try {
      await api.patch(`/site/reviews/${r.id}`, { publicationAuthorized: !r.publicationAuthorized });
      await qc.invalidateQueries({ queryKey: ['site', 'reviews'] });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }
  async function remove(r: ReviewRow) {
    try {
      await api.del(`/site/reviews/${r.id}`);
      toast.success('Avaliação excluída');
      await qc.invalidateQueries({ queryKey: ['site', 'reviews'] });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  const pages = q.data ? Math.max(1, Math.ceil(q.data.total / q.data.pageSize)) : 1;
  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Só avaliações autorizadas aparecem no site.</p>
        <Button size="sm" onClick={() => { setEditing(null); setOpen(true); }}><Plus aria-hidden /> Nova avaliação</Button>
      </div>
      {q.isPending && <LoadingState />}
      {q.isError && <ErrorState error={q.error} onRetry={() => q.refetch()} />}
      {q.data && q.data.data.length === 0 && (
        <EmptyState title="Nenhuma avaliação ainda" description="Cadastre depoimentos de clientes que autorizaram a divulgação." action={<Button size="sm" onClick={() => { setEditing(null); setOpen(true); }}>Cadastrar avaliação</Button>} />
      )}
      <ul className="grid gap-2">
        {q.data?.data.map((r) => (
          <li key={r.id} className="flex flex-wrap items-start gap-3 rounded-lg border bg-card p-3">
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                {r.customerName}
                <span role="img" aria-label={`${r.rating} de 5`} className="inline-flex text-brass">
                  {[1, 2, 3, 4, 5].map((n) => <Star key={n} className="size-3.5" style={{ fill: n <= r.rating ? 'currentColor' : 'transparent' }} aria-hidden />)}
                </span>
                <Badge variant={r.publicationAuthorized ? 'secondary' : 'outline'}>{r.publicationAuthorized ? 'Publicável' : 'Não autorizada'}</Badge>
              </p>
              <p className="mt-1 text-sm">{r.comment}</p>
              <p className="mt-1 text-xs text-muted-foreground">{fmtDate(r.createdAt, tz)}</p>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" onClick={() => toggle(r)}>{r.publicationAuthorized ? 'Despublicar' : 'Autorizar'}</Button>
              <Button size="icon-sm" variant="ghost" aria-label={`Editar avaliação de ${r.customerName}`} onClick={() => { setEditing(r); setOpen(true); }}><Pencil /></Button>
              <ConfirmDialog
                trigger={<Button size="icon-sm" variant="ghost" className="text-destructive" aria-label={`Excluir avaliação de ${r.customerName}`}><Trash2 /></Button>}
                title="Excluir esta avaliação?"
                description="Ela deixa de aparecer no site e não pode ser recuperada."
                confirmLabel="Excluir avaliação"
                onConfirm={() => remove(r)}
              />
            </div>
          </li>
        ))}
      </ul>
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
          <span className="tabular">{page} de {pages}</span>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)}>Próxima</Button>
        </div>
      )}
      <ReviewModal open={open} onOpenChange={setOpen} review={editing} />
    </div>
  );
}
